import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { id: "about", label: "01 / About" },
  { id: "loadout", label: "02 / Loadout" },
  { id: "contact", label: "03 / Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleNavClick = () => setMenuOpen(false);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.65, 0, 0.35, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "nav-glass border-b border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.4)]"
            : "border-b border-white/5 bg-transparent"
        }`}
        data-testid="main-nav"
      >
        <div className="px-6 md:px-10 py-4 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em]">
          <a href="#top" className="flex items-center gap-3 hover-underline group" data-testid="nav-logo">
            <span className="text-[#F47B20] group-hover:drop-shadow-[0_0_8px_rgba(244,123,32,0.5)] transition-all">◈</span>
            <span className="text-white/90 group-hover:text-white transition-colors">AEONEUH</span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                className="relative px-3 py-2 text-white/60 hover:text-[#F47B20] transition-all duration-300 group"
                data-testid={`nav-link-${l.id}`}
              >
                <span className="relative z-10">{l.label}</span>
                <span className="absolute inset-0 bg-[#F47B20]/0 group-hover:bg-[#F47B20]/5 transition-colors duration-300" />
              </a>
            ))}
          </nav>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden flex flex-col gap-1.5 p-2 -mr-2 min-h-[44px] min-w-[44px] items-center justify-center"
            aria-label="Menu"
            data-testid="nav-hamburger"
          >
            <motion.span
              animate={menuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
              className="block w-6 h-[1.5px] bg-white/80 origin-center"
            />
            <motion.span
              animate={menuOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
              className="block w-6 h-[1.5px] bg-white/80"
            />
            <motion.span
              animate={menuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
              className="block w-6 h-[1.5px] bg-white/80 origin-center"
            />
          </button>
        </div>
      </motion.header>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#09090b]/95 backdrop-blur-xl md:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col items-center justify-center h-full gap-8 font-mono text-sm uppercase tracking-[0.3em]">
              {links.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={handleNavClick}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="text-white/70 hover:text-[#F47B20] transition-colors py-3 px-6 min-h-[44px] flex items-center"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
